import { LitElement, html, nothing } from 'lit'
import { customElement, state, query } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { style } from './tile-configurator-copy-modal.style.js'
import { modalStyle } from './tile-configurator-modal.style.js'
import {
  AreaLayoutContext,
  CanvasStateContext,
  CustomizedMobileModesContext,
  TileConfiguratorActionsContext,
  type AreaLayoutContextType,
  type CanvasStateContextType,
  type CustomizedMobileModesContextType,
  type TileConfiguratorActionsContextType,
} from 'components/shared/product-tile-configurator/context.js'
import { TILE_VIEWS, editModeForView, isPreviewOnlyView, viewTitle, writeModeForView, type TileView } from 'components/shared/product-tile-configurator/editor-state.js'
import { countWidgets, previewWidgetCopy, sharedWidgetTypeCount, type CopyableView } from 'components/shared/product-tile-configurator/copy-widgets.js'

export interface WidgetsCopiedDetail {
  view: TileView
  added: number
}

interface CopyPlan {
  target: TileView
  added: number
  unmatched: number
  sharedTypes: number
}

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`

@customElement('csc-tile-configurator-copy-modal')
export class CSCTileConfiguratorCopyModal extends LitElement {
  static override styles = [hostStyle, modalStyle, style]

  @consume({ context: TileConfiguratorActionsContext })
  private actions!: TileConfiguratorActionsContextType

  @consume({ context: CanvasStateContext, subscribe: true })
  private sectionsByMode!: CanvasStateContextType

  @consume({ context: CustomizedMobileModesContext, subscribe: true })
  private customizedMobileModes: CustomizedMobileModesContextType = []

  @consume({ context: AreaLayoutContext, subscribe: true })
  private areaLayoutByMode!: AreaLayoutContextType

  @state()
  private sourceView: TileView | null = null

  @state()
  private targetValue: string | null = null

  @state()
  private skipExistingTypes = true

  @query('cse-modal')
  private modal!: any

  async open(sourceView: TileView) {
    this.sourceView = sourceView
    this.targetValue = null
    this.skipExistingTypes = true
    await this.updateComplete
    await this.modal?.open()
  }

  async close() {
    await this.modal?.close()
  }

  private viewState(view: TileView): CopyableView {
    const mode = editModeForView(view, this.customizedMobileModes)
    return { sections: this.sectionsByMode[mode], areaLayout: this.areaLayoutByMode[mode] }
  }

  private get targetView(): TileView | undefined {
    return TILE_VIEWS.find(view => view.value === this.targetValue)
  }

  private handleTargetChange(event: Event & { value?: unknown }) {
    if (typeof event.value === 'string') this.targetValue = event.value
  }

  private handleConfirm() {
    const source = this.sourceView
    const target = this.targetView
    if (!source || !target) return
    const added = this.actions.copyWidgets(editModeForView(source, this.customizedMobileModes), writeModeForView(target), { skipExistingTypes: this.skipExistingTypes })
    this.dispatchEvent(new CustomEvent<WidgetsCopiedDetail>('widgets-copied', {
      detail: { view: target, added },
      bubbles: true,
      composed: true,
    }))
    void this.close()
  }

  private targetDescription(target: TileView): string {
    if (isPreviewOnlyView(target, this.customizedMobileModes)) return 'Uses the desktop layout · will be customized first'
    const placed = countWidgets(this.viewState(target).sections)
    return placed ? `${plural(placed, 'widget')} already placed` : 'No widgets placed yet'
  }

  private renderTarget(target: TileView) {
    return html`
      <cse-radio-button
        class="copy-target ${this.targetValue === target.value ? 'selected' : ''}"
        value=${target.value}>
        <span class="copy-target-text">
          <cse-text class="copy-target-title" variant="body">${viewTitle(target)}</cse-text>
          <cse-text class="copy-target-description" variant="body-s">${this.targetDescription(target)}</cse-text>
        </span>
      </cse-radio-button>
    `
  }

  private copyPlan(source: TileView, target: TileView): CopyPlan {
    const sourceState = this.viewState(source)
    const targetState = this.viewState(target)
    const { added, unmatched } = previewWidgetCopy(sourceState, targetState, { skipExistingTypes: this.skipExistingTypes })
    return { target, added, unmatched, sharedTypes: sharedWidgetTypeCount(sourceState.sections, targetState.sections) }
  }

  private renderSummary({ target, added, unmatched, sharedTypes }: CopyPlan) {
    return html`
      <div class="copy-summary">
        <cse-text variant="body-s">
          ${added
            ? `${plural(added, 'widget')} will be added to the matching zones in ${viewTitle(target)}, after any widgets already there.`
            : `Nothing to add — every widget type is already in ${viewTitle(target)}.`}
        </cse-text>
        ${unmatched ? html`
          <cse-text class="copy-summary-warning" variant="body-s">
            ${plural(unmatched, 'widget')} can't be copied — ${viewTitle(target)} has no matching zone for them.
          </cse-text>
        ` : nothing}
        ${sharedTypes ? html`
          <cse-checkbox
            class="copy-skip-existing"
            .checked=${this.skipExistingTypes}
            @change=${(event: Event & { value?: unknown }) => { this.skipExistingTypes = event.value === true }}>
            Skip ${plural(sharedTypes, 'widget type')} already in that view
          </cse-checkbox>
        ` : nothing}
      </div>
    `
  }

  override render() {
    const source = this.sourceView
    const target = this.targetView
    const plan = source && target ? this.copyPlan(source, target) : null

    return html`
      <cse-modal>
        <div slot="header" class="configurator-modal-header copy-header">
          <cse-text class="configurator-modal-title" variant="heading-3">Copy widgets to another view</cse-text>
        </div>

        ${source ? html`
          <div class="copy-body">
            <cse-text class="configurator-modal-description copy-intro" variant="body-s">
              Add ${plural(countWidgets(this.viewState(source).sections), 'widget')} (incl. settings) from ${viewTitle(source)} to the selected views.
              Nothing will be removed or replaced.
            </cse-text>
            <cse-text class="copy-label" variant="caption-m">Copy to</cse-text>
            <cse-radio-button-group
              class="copy-targets"
              name="copy-target"
              value=${this.targetValue ?? ''}
              @change=${this.handleTargetChange}>
              ${TILE_VIEWS.filter(view => view !== source).map(view => this.renderTarget(view))}
            </cse-radio-button-group>
            ${plan ? this.renderSummary(plan) : nothing}
          </div>
        ` : nothing}

        <div slot="footer" class="copy-footer">
          <cse-button variant="default" size="small" class="copy-cancel" @click=${() => this.close()}>Cancel</cse-button>
          <cse-button
            variant="primary"
            size="small"
            class="copy-confirm"
            ?disabled=${!plan?.added}
            @click=${this.handleConfirm}>
            ${plan ? `Add ${plural(plan.added, 'widget')}` : 'Add widgets'}
          </cse-button>
        </div>
      </cse-modal>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-configurator-copy-modal': CSCTileConfiguratorCopyModal
  }
}
